import {generateAnonymousId} from '../../anonymousId'
import {parseQueryString, removeURLParameter} from '../../utils/url'
import {getCookie, setCookie} from '../utils/cookies'
import {isBrowser} from '../utils/environment'
import {warnServerEnvironment} from '../utils/logging/server-warning'
import cookieConfig from './cookieConfig'

/**
 * Remove the 'anonymousId' parameter from the current URL without reloading the page.
 */
export const removeAnonymousIdFromUrl = () => {
  if (!isBrowser) return
  const newUrl = removeURLParameter(location.href, 'anonymousId')
  history.replaceState(history.state, '', newUrl)
}

const getFromQueryString = () => {
  if (!isBrowser) {
    warnServerEnvironment(`getQueryStringObject`)
    return null
  }

  const {anonymousId} = parseQueryString(location?.search || '', true)
  return anonymousId as string
}

const getFromCookie = () => getCookie(cookieConfig.name)

export const getAnonymousId = () => getFromQueryString() || getFromCookie()

/**
 * AnonymousId logic steps:
 *
 * 1 - Query string parameter: check if anonymousId parameter is present in the URL and use its value.
 * 2 - Cookie: check if anonymousId is already stored in a cookie and use its value.
 * 3 - Generate: use UUID to generate a new anonymousId.
 */
export const getOrGenerateAnonymousId = (): string =>
  getAnonymousId() || generateAnonymousId()

/**
 * Persist the anonymousId in a cookie
 * @param anonymousId
 */
export const persistAnonymousId = (anonymousId: string) => {
  const {secure, sameSite, domain, maxAge: expires} = cookieConfig.options

  setCookie(cookieConfig.name, anonymousId, {
    expires,
    secure,
    sameSite,
    domain
  })
}

/**
 * Check if the given anonymous ID indicates a synthetic test
 * @param anonymousId - The anonymous ID to check
 * @returns true if this is a synthetic test, false otherwise
 */
export const isSyntheticTestAnonymousId = (anonymousId: string): boolean => {
  return anonymousId?.startsWith('test_website_synthetics') ?? false
}
